import apis from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { AppDispatch } from '..';
import logger from '../../utils/logger';
import { translate } from '../../i18n/translate';

export const connectTeamToResource = (
  resourceType: string,
  resourceUuid: string,
  teamUuid: string,
  memberOverrides?: { user_uuid: string; access: string }[]
) => async (dispatch: AppDispatch) => {
  try {
    const response = await apis.post(
      `/collaborators/${resourceType}/${resourceUuid}/connect-team`,
      { team_uuid: teamUuid, member_overrides: memberOverrides || [] }
    );
    if (response.data?.success) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: `Team connected (${response.data.connected_count} members)`,
        showModal: true,
        duration: 3000,
      }));
      return response.data;
    }
  } catch (error: any) {
    logger.error('[COLLAB] Connect team failed', { error: error?.message });
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: translate('Failed to connect team'),
      showModal: true,
      duration: 4000,
    }));
  }
  return null;
};

export const disconnectTeamFromResource = (
  resourceType: string,
  resourceUuid: string,
  teamUuid: string
) => async (dispatch: AppDispatch) => {
  try {
    const response = await apis.delete(
      `/collaborators/${resourceType}/${resourceUuid}/disconnect-team/${teamUuid}`
    );
    if (response.data) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: translate('Team disconnected'),
        showModal: true,
        duration: 3000,
      }));
      return response.data;
    }
  } catch (error: any) {
    logger.error('[COLLAB] Disconnect team failed', { error: error?.message });
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: translate('Failed to disconnect team'),
      showModal: true,
      duration: 4000,
    }));
  }
  return null;
};

/**
 * Lightweight teams list - only names, UUIDs, member counts.
 * No full member decryption, much faster than /org-people/teams.
 */
export const fetchTeamsListLightweight = () => async (_dispatch: AppDispatch) => {
  try {
    const response = await apis.get('/collaborators/teams-list');
    return response.data?.teams || [];
  } catch (error: any) {
    logger.error('[COLLAB] Fetch teams list failed', { error: error?.message });
    return [];
  }
};

export const fetchConnectedTeams = (
  resourceType: string,
  resourceUuid: string
) => async (_dispatch: AppDispatch) => {
  try {
    const response = await apis.get(
      `/collaborators/${resourceType}/${resourceUuid}/connected-teams`
    );
    return response.data?.teams || [];
  } catch (error: any) {
    logger.error('[COLLAB] Fetch connected teams failed', { error: error?.message });
    return [];
  }
};

/* ── Departments as collaborators — the same generic connect/disconnect as
      teams, with source='department' and default view access (G-6). ── */

export const connectDepartmentToResource = (
  resourceType: string,
  resourceUuid: string,
  departmentUuid: string,
  memberOverrides?: { user_uuid: string; access: string }[]
) => async (dispatch: AppDispatch) => {
  try {
    const response = await apis.post(
      `/collaborators/${resourceType}/${resourceUuid}/connect-department`,
      { department_uuid: departmentUuid, member_overrides: memberOverrides || [] }
    );
    if (response.data?.success) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: `Department connected (${response.data.connected_count} members)`,
        showModal: true,
        duration: 3000,
      }));
      return response.data;
    }
  } catch (error: any) {
    logger.error('[COLLAB] Connect department failed', { error: error?.message });
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: translate('Failed to connect department'),
      showModal: true,
      duration: 4000,
    }));
  }
  return null;
};

export const disconnectDepartmentFromResource = (
  resourceType: string,
  resourceUuid: string,
  departmentUuid: string
) => async (dispatch: AppDispatch) => {
  try {
    const response = await apis.delete(
      `/collaborators/${resourceType}/${resourceUuid}/disconnect-department/${departmentUuid}`
    );
    if (response.data) {
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: translate('Department disconnected'),
        showModal: true,
        duration: 3000,
      }));
      return response.data;
    }
  } catch (error: any) {
    logger.error('[COLLAB] Disconnect department failed', { error: error?.message });
    dispatch(showFeedbackModal({
      modalType: 'error',
      message: translate('Failed to disconnect department'),
      showModal: true,
      duration: 4000,
    }));
  }
  return null;
};

/** Lightweight departments list - names, UUIDs, member counts (mirrors teams). */
export const fetchDepartmentsListLightweight = () => async (_dispatch: AppDispatch) => {
  try {
    const response = await apis.get('/collaborators/departments-list');
    return response.data?.departments || [];
  } catch (error: any) {
    logger.error('[COLLAB] Fetch departments list failed', { error: error?.message });
    return [];
  }
};

export const fetchConnectedDepartments = (
  resourceType: string,
  resourceUuid: string
) => async (_dispatch: AppDispatch) => {
  try {
    const response = await apis.get(
      `/collaborators/${resourceType}/${resourceUuid}/connected-departments`
    );
    return response.data?.departments || [];
  } catch (error: any) {
    logger.error('[COLLAB] Fetch connected departments failed', { error: error?.message });
    return [];
  }
};
